import React, { useEffect, useState } from 'react';
import { GitBranch, AlertCircle, Shield, Info, X, ArrowRight, ArrowLeft, Trash2, Users } from 'lucide-react';
import AlertModal from "../../components/AlertModal";
import { useEscalationGroupMapping } from "./hooks/useEscalationGroupMapping";
import MappingForm from "./components/MappingForm";
import { SkeletonStyles, SkeletonBox, LoadingPill } from "../../components/SkeletonLoader";

function EscalationGroupMapping() {
    const {
        theme,
        language,
        fonts,
        groups,
        loading,
        mappings,
        mappingsLoading,
        handleDeleteMapping,
        profileLoading,
        canMapEscalation,
        alertModal,
        setAlertModal,
        formData,
        formErrors,
        styles,
        t,
        handleSubmit,
        handleInputChange,
        getGroupById,
        resetForm,
    } = useEscalationGroupMapping();

    // The page previously gated its skeleton on `profileLoading` alone, which
    // resolves almost instantly whenever the profile was already cached from
    // an earlier page (useProfile's module-level cache) — so the skeleton
    // essentially never appeared in practice. Gate on the actual groups fetch
    // instead: wait for profile to resolve, then (for admin/superadmin) wait
    // for `loading` to finish; other roles never fetch, so they're ready
    // immediately. This can never get stuck since every branch reaches true.
    const [pageReady, setPageReady] = useState(false);
    const [showInfoModal, setShowInfoModal] = useState(false);
    useEffect(() => {
        if (profileLoading) return;
        if (canMapEscalation) {
            if (!loading) setPageReady(true);
        } else {
            setPageReady(true);
        }
    }, [profileLoading, canMapEscalation, loading]);

    if (!pageReady) {
        const isRTL = language === "ar";
        return (
            <div style={styles.container}>
                <SkeletonStyles theme={theme} />
                <LoadingPill
                    text={isRTL ? "جارٍ تحميل بيانات التعيين..." : "Loading mapping information..."}
                    theme={theme}
                    isRTL={isRTL}
                />

                {/* Header skeleton */}
                <div style={styles.header}>
                    <SkeletonBox width="260px" height="30px" style={{ marginBottom: "10px" }} />
                    <SkeletonBox width="340px" height="18px" />
                </div>

                {/* Info box skeleton */}
                <div style={styles.infoBox}>
                    <SkeletonBox width="100%" height="20px" />
                </div>

                {/* Form card skeleton */}
                <div style={styles.formCard}>
                    <div style={styles.formGrid}>
                        <SkeletonBox width="100%" height="70px" style={{ borderRadius: "12px" }} />
                        <SkeletonBox width="100%" height="70px" style={{ borderRadius: "12px" }} />
                    </div>
                    <div style={styles.formGrid}>
                        <SkeletonBox width="100%" height="70px" style={{ borderRadius: "12px" }} />
                        <SkeletonBox width="100%" height="70px" style={{ borderRadius: "12px" }} />
                    </div>
                    <div style={styles.buttonGroup}>
                        <SkeletonBox width="100%" height="48px" style={{ borderRadius: "12px" }} />
                        <SkeletonBox width="100%" height="48px" style={{ borderRadius: "12px" }} />
                    </div>
                </div>
            </div>
        );
    }

    // Gate on the granted 'complaints' → 'mapEscalation' feature (real
    // admin/superadmin accounts always pass via hasFeatureAccess's bypass).
    if (!canMapEscalation) {
        return (
            <div style={styles.container}>
                <div style={styles.header}>
                    <h1 style={styles.title}>
                        <Shield size={28} />
                        {t.adminOnly}
                    </h1>
                </div>
                <div style={styles.emptyState}>
                    <Shield size={64} style={{ opacity: 0.3, marginBottom: "1rem" }} />
                    <div style={{ fontSize: fonts.subtitle, fontWeight: "600" }}>
                        {t.unauthorized}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            {/* AlertModal for errors and success */}
            <AlertModal
                open={alertModal.open}
                title={alertModal.title}
                message={alertModal.message}
                variant={alertModal.variant}
                onConfirm={() => setAlertModal({ ...alertModal, open: false })}
                onClose={() => setAlertModal({ ...alertModal, open: false })}
                theme={theme}
                language={language}
            />

            {/* Welcome Card */}
            <div style={{
                ...styles.header,
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                borderRadius: "24px",
                padding: "32px",
                marginTop: "-43px",
                marginBottom: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                animation: "slideInDown 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)",
                flexWrap: "wrap",
                gap: "24px",
                direction: language === "ar" ? "rtl" : "ltr",
            }}>
                <div style={{ flex: 1, minWidth: "250px" }}>
                    <h1 style={{
                        fontSize: "28px",
                        fontWeight: "700",
                        color: theme === "dark" ? "#f1f5f9" : "#1e293b",
                        margin: "0 0 12px 0",
                        letterSpacing: "-0.5px",
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                    }}>
                        <GitBranch size={28} />
                        {t.title}
                    </h1>
                    <p style={{
                        fontSize: "15px",
                        color: theme === "dark" ? "#cbd5e1" : "#64748b",
                        margin: 0,
                        fontWeight: "500",
                    }}>
                        {t.subtitle}
                    </p>
                </div>
                <button
                    style={{
                        background: theme === "dark"
                            ? "linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.1) 100%)"
                            : "linear-gradient(135deg, rgba(96, 165, 250, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)",
                        border: theme === "dark"
                            ? "1px solid rgba(59, 130, 246, 0.3)"
                            : "1px solid rgba(59, 130, 246, 0.2)",
                        width: "48px",
                        height: "48px",
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        color: theme === "dark" ? "#60a5fa" : "#2563eb",
                        flexShrink: 0,
                    }}
                    onClick={() => setShowInfoModal(true)}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = theme === "dark"
                            ? "linear-gradient(135deg, rgba(59, 130, 246, 0.25) 0%, rgba(37, 99, 235, 0.2) 100%)"
                            : "linear-gradient(135deg, rgba(96, 165, 250, 0.2) 0%, rgba(59, 130, 246, 0.15) 100%)";
                        e.currentTarget.style.transform = "scale(1.05)";
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = theme === "dark"
                            ? "linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.1) 100%)"
                            : "linear-gradient(135deg, rgba(96, 165, 250, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)";
                        e.currentTarget.style.transform = "scale(1)";
                    }}
                    title={language === "ar" ? "معلومات" : "Information"}
                >
                    <Info size={24} />
                </button>
            </div>

            {/* Info Modal */}
            {showInfoModal && (
                <>
                    <div
                        style={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 9998,
                        }}
                        onClick={() => setShowInfoModal(false)}
                    />
                    <div style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: "rgba(0, 0, 0, 0.65)",
                        backdropFilter: "blur(12px)",
                        WebkitBackdropFilter: "blur(12px)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 9999,
                        padding: "20px",
                        animation: "fadeIn 0.3s ease-out",
                    }} onClick={() => setShowInfoModal(false)}>
                        <div style={{
                            background: theme === "dark"
                                ? "rgba(15, 23, 42, 0.95)"
                                : "rgba(255, 255, 255, 0.98)",
                            borderRadius: "24px",
                            padding: "32px",
                            maxWidth: "500px",
                            width: "100%",
                            border: theme === "dark"
                                ? "1px solid rgba(148, 163, 184, 0.15)"
                                : "1px solid rgba(0, 0, 0, 0.05)",
                            boxShadow: theme === "dark"
                                ? "0 20px 60px rgba(0, 0, 0, 0.4)"
                                : "0 20px 60px rgba(0, 0, 0, 0.15)",
                            animation: "slideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                        }} onClick={(e) => e.stopPropagation()}>
                            <div style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "24px",
                            }}>
                                <h2 style={{
                                    fontSize: "22px",
                                    fontWeight: "700",
                                    color: theme === "dark" ? "#f1f5f9" : "#1e293b",
                                    margin: 0,
                                }}>
                                    {language === "ar" ? "معلومات التعيين" : "Mapping Information"}
                                </h2>
                                <button
                                    style={{
                                        background: "transparent",
                                        border: "none",
                                        cursor: "pointer",
                                        padding: "8px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        borderRadius: "8px",
                                        transition: "all 0.2s ease",
                                        color: theme === "dark" ? "#94a3b8" : "#64748b",
                                    }}
                                    onClick={() => setShowInfoModal(false)}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = theme === "dark"
                                            ? "rgba(148, 163, 184, 0.1)"
                                            : "rgba(0, 0, 0, 0.05)";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = "transparent";
                                    }}
                                >
                                    <X size={24} />
                                </button>
                            </div>
                            <div style={{
                                fontSize: "15px",
                                lineHeight: "1.7",
                                color: theme === "dark" ? "#cbd5e1" : "#475569",
                                whiteSpace: "pre-line",
                            }}>
                                {t.mappingInfo}
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Main Form */}
            <div style={styles.formCard}>
                <MappingForm
                    styles={styles}
                    fonts={fonts}
                    t={t}
                    theme={theme}
                    language={language}
                    groups={groups}
                    loading={loading}
                    formData={formData}
                    formErrors={formErrors}
                    handleSubmit={handleSubmit}
                    handleInputChange={handleInputChange}
                    getGroupById={getGroupById}
                    resetForm={resetForm}
                />
            </div>

            {/* Current Mappings — who escalates to whom (QA parity issue #7).
                The page used to be create-only with no way to see existing
                routing; this section renders GET /api/escalations/mappings. */}
            <div style={{ ...styles.formCard, marginTop: "24px", direction: language === "ar" ? "rtl" : "ltr" }}>
                <div style={{ marginBottom: "16px" }}>
                    <h2 style={{
                        fontSize: "20px",
                        fontWeight: "700",
                        color: theme === "dark" ? "#f1f5f9" : "#1e293b",
                        margin: "0 0 6px 0",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                    }}>
                        <Users size={20} />
                        {t.currentMappings}
                    </h2>
                    <p style={{
                        fontSize: "14px",
                        color: theme === "dark" ? "#94a3b8" : "#64748b",
                        margin: 0,
                    }}>
                        {t.currentMappingsSubtitle}
                    </p>
                </div>

                {mappingsLoading ? (
                    <SkeletonBox width="100%" height="56px" style={{ borderRadius: "12px" }} />
                ) : mappings.length === 0 ? (
                    <div style={{
                        padding: "24px",
                        textAlign: "center",
                        color: theme === "dark" ? "#94a3b8" : "#64748b",
                        fontSize: "14px",
                    }}>
                        <AlertCircle size={28} style={{ opacity: 0.4, marginBottom: "8px" }} />
                        <div>{t.noMappings}</div>
                    </div>
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        {mappings.map((m) => (
                            <div
                                key={m.id}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: "12px",
                                    padding: "14px 16px",
                                    borderRadius: "12px",
                                    border: theme === "dark"
                                        ? "1px solid rgba(148, 163, 184, 0.15)"
                                        : "1px solid rgba(0, 0, 0, 0.06)",
                                    background: theme === "dark"
                                        ? "rgba(30, 41, 59, 0.5)"
                                        : "rgba(248, 250, 252, 0.9)",
                                    flexWrap: "wrap",
                                }}
                            >
                                <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", flex: 1, minWidth: "220px" }}>
                                    <span style={{
                                        fontWeight: "600",
                                        fontSize: "14px",
                                        color: theme === "dark" ? "#e2e8f0" : "#1e293b",
                                    }}>
                                        {m.issuerGroupName ?? m.issuerGroupId}
                                    </span>
                                    {language === "ar"
                                        ? <ArrowLeft size={16} color={theme === "dark" ? "#60a5fa" : "#2563eb"} />
                                        : <ArrowRight size={16} color={theme === "dark" ? "#60a5fa" : "#2563eb"} />}
                                    <span style={{
                                        fontWeight: "600",
                                        fontSize: "14px",
                                        color: theme === "dark" ? "#e2e8f0" : "#1e293b",
                                    }}>
                                        {m.assignedGroupName ?? m.assignedGroupId}
                                    </span>
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <span style={{
                                        fontSize: "12px",
                                        fontWeight: "600",
                                        padding: "4px 10px",
                                        borderRadius: "999px",
                                        background: theme === "dark" ? "rgba(59, 130, 246, 0.15)" : "rgba(59, 130, 246, 0.1)",
                                        color: theme === "dark" ? "#93c5fd" : "#2563eb",
                                    }}>
                                        {m.type}
                                    </span>
                                    <span style={{
                                        fontSize: "12px",
                                        fontWeight: "600",
                                        padding: "4px 10px",
                                        borderRadius: "999px",
                                        background: m.priority === "high"
                                            ? "rgba(239, 68, 68, 0.12)"
                                            : m.priority === "medium"
                                                ? "rgba(245, 158, 11, 0.12)"
                                                : "rgba(34, 197, 94, 0.12)",
                                        color: m.priority === "high"
                                            ? "#ef4444"
                                            : m.priority === "medium"
                                                ? "#d97706"
                                                : "#16a34a",
                                    }}>
                                        {m.priority}
                                    </span>
                                    <button
                                        onClick={() => handleDeleteMapping(m.id)}
                                        title={t.deleteMapping}
                                        aria-label={t.deleteMapping}
                                        style={{
                                            background: "transparent",
                                            border: "none",
                                            cursor: "pointer",
                                            padding: "6px",
                                            borderRadius: "8px",
                                            display: "flex",
                                            alignItems: "center",
                                            color: theme === "dark" ? "#f87171" : "#dc2626",
                                        }}
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <style>{`
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
                @keyframes slideInDown {
                    from {
                        opacity: 0;
                        transform: translateY(-20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                @keyframes fadeIn {
                    from {
                        opacity: 0;
                    }
                    to {
                        opacity: 1;
                    }
                }
            `}</style>
        </div>
    );
}

export default EscalationGroupMapping;